import React, { useEffect, useState, useContext } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Autocomplete,
  TextField
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ButtonTheme from '../../components/ThemeButton/ThemeButton';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ApiService from '../../functions/apiService';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Multiselect from 'multiselect-react-dropdown';
import CollapseComponent from '../../components/Schedule/CollapseComponent';
import ButtonDatePicker from '../../components/DatePickers/ButtonDatePicker';
import './reports.css';
import { ExportCsvButton } from '../../components/Buttons/ExportCsvButton';
import AuthContext from '../../context/AuthContext';

const EmployeeWorkHoursReportf = () => {
  const navigate = useNavigate();
  const ReqComponent = () => <span className="text-danger">*</span>;
  const { v3Data } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(moment().startOf('month').toDate());
  const [toDate, setToDate] = useState(moment().toDate());
  const [employees, setEmployees] = useState([]);
  const [absentCodes, setAbsentCodes] = useState([]);
  const [selectedAbsentCode, setSelectedAbsentCode] = useState(null);

  const shiftColumns = [
    {
      field: 'date',
      headerName: 'Date',
      flex: 1
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1
    },
    {
      field: 'work_code',
      headerName: 'Work Code',
      flex: 1
    },
    {
      field: 'hours',
      headerName: 'Hours',
      flex: 1
    }
  ];

  const backFunction = () => {
    navigate('/firehouse/reports');
  };

  const getEmployeeSwapReport = (method) => {
    setLoading(true);


    const payload = {
      start_date: moment(fromDate).format('YYYY-MM-DD'),
      end_date: moment(toDate).format('YYYY-MM-DD'),
      absent_code: '',
      method
    };

    if (selectedAbsentCode) {
      payload.absent_code = selectedAbsentCode.value;
    }

    ApiService.post('/report/employee-work-hours', payload)
      .then((response) => {
        setLoading(false);

        if (!method) {
          const apiData = response?.data || {};

          const transformedRows = Object.keys(apiData).map(rank => ({
            rank,
            records: apiData[rank]
          }));

          setRows(transformedRows);
          return;
        }

        if (method === 'csv') {
          const blob = new Blob([response], {
            type: 'text/csv;charset=utf-8;'
          });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'Employee-Work-Hours-Report.csv';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return;
        }

        if (method === 'pdf') {
          const pdfUrl = response?.pdf_url;
          if (pdfUrl) {
            window.open(pdfUrl, '_blank');
          } else {
            toast.error('PDF URL not found');
          }
        }
      })
      .catch(err => {
        setLoading(false);
        toast.error(err?.response?.data?.message || 'Failed to fetch data');
      });
  };

  useEffect(() => {
    const absentCodesUrl = v3Data?.V3_INCLUDE_ALL_ABSENT_CODE_IN_EMP_WORK_HOUR_REPORT === 'true'
      ? '/absentCodesConditions'
      : '/absentCodesConditions?condition=EMPLOYEE_WORK_HOUR_REPORT_ABSENT_CODE';
    ApiService.get(absentCodesUrl)
      .then(({ data }) => {
        const codesObj = data?.data || {};
        const options = Object.keys(codesObj).map(code => ({
          label: code,
          value: code
        }));
        setAbsentCodes(options);
      })
      .catch(() => {
        toast.error('Failed to load absent codes');
      });
  }, [v3Data]);

  useEffect(() => {
    getEmployeeSwapReport();
    loadEmployees();
  }, []);

  const loadEmployees = () => {
    setLoading(true);
    ApiService.get('/employeeToSwap')
      .then(({ data }) => {
        const tempArr = data.map(emp => ({
          label: `${emp.EA_Rank} ${emp.fullName}`,
          value: emp.Emp_Code
        }));
        setEmployees(tempArr);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  return (
    <Paper className="p-4" sx={{ marginTop: '1rem' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          marginBottom: '25px'
        }}
      >
        {/* Back button */}
        <Button onClick={backFunction} sx={{ color: '#212529', minWidth: 'auto' }} style={{ padding: '0px' }}>
          <ArrowBackIosIcon fontSize="small" />
        </Button>

        {/* Title */}
        <Typography variant="h5">
          Employee Work Hours Report
        </Typography>

        {/* Right-aligned actions */}
        {rows.length > 0 && (
          <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 1 }}>
            <ExportCsvButton
              onClick={() => getEmployeeSwapReport('csv')}
              text={'Export CSV'}
            />
            <ExportCsvButton
              onClick={() => getEmployeeSwapReport('pdf')}
              text={'Export PDF'}
            />
          </Box>
        )}
      </Box>

      <Grid container spacing={2} alignItems="flex-end" className="mt-1">
        <Grid item xs={12} sm={6} md={2.5} className="d-grid">
          <label className="my-1">From<ReqComponent /></label>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <ButtonDatePicker
              className="w-100"
              label={moment(fromDate).format('MM-DD-YYYY')}
              value={fromDate}
              onChange={(e) => setFromDate(e)}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} sm={6} md={2.5} className="d-grid">
          <label className="my-1">To<ReqComponent /></label>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <ButtonDatePicker
              className="w-100"
              label={moment(toDate).format('MM-DD-YYYY')}
              value={toDate}
              onChange={(e) => setToDate(e)}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} md={2.5}>
          <label className="my-1">Absent Codes</label>
          <Autocomplete
            options={absentCodes}
            getOptionLabel={(option) => option.label}
            value={selectedAbsentCode}
            onChange={(e, val) => setSelectedAbsentCode(val)}
            isOptionEqualToValue={(option, value) =>
              option?.value === value?.value
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Absent Code"
                size="small"
                sx={{
                  '& .MuiInputBase-root': {
                    height: '50px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                  }
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={2} sx={{ display: 'flex', flexDirection: 'column', marginBottom: '8px' }}>
          <Button
            variant="contained"
            onClick={() => getEmployeeSwapReport()}
            sx={{ whiteSpace: 'nowrap', height: '38px', width: '100px' }}
          >
            Find
          </Button>
        </Grid>
      </Grid>

      {loading && (
        <Box className="d-flex justify-content-center mt-5">
          <CircularProgress />
        </Box>
      )}

      {!loading && rows.length === 0 && (
        <Box
          className="d-flex flex-column align-items-center justify-content-center"
          sx={{ height: 300 }}
        >
          <img src="/images/blur-logo.png" height={180} alt="No data" />
          <Typography sx={{ mt: 2, color: '#555' }}>
            No Data Found
          </Typography>
        </Box>
      )}

      {!loading && rows.length > 0 && (
        <div>
          {rows.map((rankGroup, index) => {
            const rankRows = rankGroup.records.map((item, i) => ({
              id: i,
              date: item.date,
              name: `${item.first_name} ${item.last_name}`,
              work_code: item.work_code,
              hours: item.hours
            }));

            const totalHours = rankRows.reduce(
              (sum, r) => sum + Number(r.hours || 0),
              0
            );

            return (
              <CollapseComponent
                key={rankGroup.rank || index}
                name={`Rank: ${rankGroup.rank}`}
                rows={[
                  ...rankRows,
                  {
                    id: 'total',
                    date: '',
                    name: '',
                    work_code: 'TOTAL HOURS',
                    hours: totalHours
                  }
                ]}
                columns={shiftColumns}
                checkboxSelection={false}
                open={true}
                height="auto"
                showHeader={true}
              />
            );
          })}
        </div>
      )}
    </Paper>
  );
};

export default EmployeeWorkHoursReportf;
